import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const Banners = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [banner, setBanner] = useState({
    enabled: false,
    id: "banner_v1",
    title: "Special Welcome Offer! 🎉",
    subtitle: "Get flat 25% off on your first home service booking with verified buddies.",
    imageUrl: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=800&auto=format&fit=crop",
    buttonText: "Explore Services →",
    actionUrl: "ServicesScreen",
  });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchBannerSettings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.status === "success" && json.data?.settings?.announcement) {
        setBanner((prev) => ({
          ...prev,
          ...json.data.settings.announcement,
        }));
      }
    } catch (err) {
      console.error("Error fetching banner settings:", err);
      showToast("Failed to load banner settings from server", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBannerSettings();
  }, [fetchBannerSettings]);

  const handleSaveBanner = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        announcement: {
          ...banner,
          enabled: Boolean(banner.enabled),
          id: banner.id || `banner_${Date.now()}`,
        },
      };

      const res = await fetch(`${BASE_URL}admin/settings`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (json.status === "success") {
        showToast("Banner saved and published successfully! 🚀", "success");
      } else {
        showToast(json.message || "Failed to save banner", "error");
      }
    } catch (err) {
      console.error("Error saving banner:", err);
      showToast("Network error saving banner", "error");
    } finally {
      setSaving(false);
    }
  };

  const admin = JSON.parse(localStorage.getItem("admin") || "{}");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  const handleGenerateNewId = () => {
    const newId = `banner_${Date.now()}`;
    setBanner((prev) => ({ ...prev, id: newId }));
    showToast(`New Banner ID generated: ${newId}. All users will see this as a fresh announcement.`, "info");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#1C1917] flex flex-col font-sans">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Container with lg:ml-72 to avoid sidebar overlap */}
      <main className="lg:ml-72 min-h-screen flex flex-col flex-1 pb-16">
        {/* Top Navbar */}
        <header className="bg-white/90 backdrop-blur-md border-b border-stone-200 px-4 sm:px-6 lg:px-8 py-5 sticky top-0 z-30 shadow-xs">
          <div className="pt-14 lg:pt-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-black text-stone-900 tracking-tight">
                    Banners & Announcements
                  </h1>
                  <p className="text-xs text-stone-500">
                    Control popup banners and marketing drawers for customer mobile app
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                  banner.enabled
                    ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                    : "bg-stone-100 text-stone-600 border border-stone-300"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full mr-2 ${
                    banner.enabled ? "bg-emerald-500 animate-pulse" : "bg-stone-400"
                  }`}
                ></span>
                {banner.enabled ? "Active in Mobile App" : "Inactive / Hidden"}
              </span>

              <button
                onClick={handleSaveBanner}
                disabled={saving || loading}
                className="bg-stone-900 hover:bg-black text-white px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 shadow-xs disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                    <span>Publishing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-[#F8D346]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Save & Publish</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Toast Alert */}
        {toast && (
          <div className="fixed top-20 right-8 z-50 animate-bounce">
            <div
              className={`px-4 py-3 rounded-xl shadow-lg text-xs font-bold flex items-center space-x-2 ${
                toast.type === "error"
                  ? "bg-rose-600 text-white"
                  : toast.type === "info"
                  ? "bg-blue-600 text-white"
                  : "bg-emerald-600 text-white"
              }`}
            >
              <span>{toast.message}</span>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Form Controls (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              {/* Activation Card */}
              <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-xs">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-black text-stone-900 uppercase tracking-wider">
                      Banner Visibility Status
                    </h2>
                    <p className="text-xs text-stone-500 mt-1">
                      Toggle ON to display this banner in the customer app's bottom drawer
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={banner.enabled}
                      onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-stone-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[4px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#FFCC0A]"></div>
                  </label>
                </div>
              </div>

              {/* Banner Details Form */}
              <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-xs space-y-5">
                <div className="flex items-center justify-between border-b border-stone-100 pb-4">
                  <h2 className="text-sm font-black text-stone-900 uppercase tracking-wider">
                    Banner Content & Settings
                  </h2>
                  <button
                    type="button"
                    onClick={handleGenerateNewId}
                    className="text-[11px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg border border-amber-200 transition-colors"
                  >
                    🔄 New Campaign ID
                  </button>
                </div>

                {/* Banner ID Tag */}
                <div>
                  <label className="block text-xs font-bold text-stone-700 mb-1">
                    Campaign Identifier (ID)
                  </label>
                  <input
                    type="text"
                    value={banner.id}
                    onChange={(e) => setBanner({ ...banner, id: e.target.value })}
                    className="w-full text-xs font-mono bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. banner_festive_offer_v1"
                  />
                  <p className="text-[11px] text-stone-400 mt-1">
                    Tip: Changing this ID causes the banner to reappear once for users who previously dismissed it.
                  </p>
                </div>

                {/* Image URL with Spec Helper */}
                <div>
                  <label className="block text-xs font-bold text-stone-700 mb-1">
                    Banner Image URL
                  </label>
                  <input
                    type="text"
                    value={banner.imageUrl}
                    onChange={(e) => setBanner({ ...banner, imageUrl: e.target.value })}
                    className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="https://example.com/banner.png or /uploads/banners/..."
                  />

                  {/* Measurement Box */}
                  <div className="mt-2.5 bg-amber-50/70 border border-amber-200/80 rounded-xl p-3 flex items-start space-x-2.5">
                    <span className="text-base">📐</span>
                    <div className="text-[11px] text-amber-900 leading-relaxed">
                      <span className="font-bold">Recommended Measurements: </span>
                      <strong>800 × 500 px</strong> (16:10 aspect ratio), top corner radius <strong>28px–32px</strong>. Keep important text 25px away from edges.
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-xs font-bold text-stone-700 mb-1">
                    Headline Title
                  </label>
                  <input
                    type="text"
                    value={banner.title}
                    onChange={(e) => setBanner({ ...banner, title: e.target.value })}
                    className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. Mega Summer Discount! ☀️"
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-xs font-bold text-stone-700 mb-1">
                    Subtitle / Offer Description
                  </label>
                  <textarea
                    rows={2}
                    value={banner.subtitle}
                    onChange={(e) => setBanner({ ...banner, subtitle: e.target.value })}
                    className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. Book any AC service or home cleaning and get flat ₹150 cashback."
                  />
                </div>

                {/* Button Text & Target */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-stone-700 mb-1">
                      Button Label
                    </label>
                    <input
                      type="text"
                      value={banner.buttonText}
                      onChange={(e) => setBanner({ ...banner, buttonText: e.target.value })}
                      className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      placeholder="e.g. Explore Services →"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-stone-700 mb-1">
                      Action Target Screen
                    </label>
                    <select
                      value={banner.actionUrl}
                      onChange={(e) => setBanner({ ...banner, actionUrl: e.target.value })}
                      className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      <option value="ServicesScreen">Services Screen (Default)</option>
                      <option value="Home">Home Screen</option>
                      <option value="BookingsScreen">Bookings Screen</option>
                      <option value="ReferAndEarn">Refer & Earn Screen</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Live Mobile Mockup Preview (5 cols) */}
            <div className="lg:col-span-5">
              <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-xs sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-black text-stone-900 uppercase tracking-wider flex items-center gap-2">
                    <span>📱</span>
                    <span>Live App Drawer Preview</span>
                  </h2>
                  <span className="text-[10px] uppercase font-bold text-stone-400 tracking-wider">
                    Customer View
                  </span>
                </div>

                {/* Realistic Phone Frame */}
                <div className="w-[310px] mx-auto bg-stone-900 rounded-[38px] p-3 shadow-2xl border-4 border-stone-800">
                  {/* Phone Screen */}
                  <div className="w-full bg-stone-100 rounded-[28px] overflow-hidden min-h-[460px] flex flex-col justify-end relative">
                    {/* Simulated Background */}
                    <div className="absolute inset-0 bg-stone-300/40 p-4 flex flex-col items-center justify-center opacity-40">
                      <div className="w-20 h-4 bg-stone-400 rounded-full mb-3"></div>
                      <div className="w-40 h-2 bg-stone-400 rounded-full mb-2"></div>
                      <div className="w-32 h-2 bg-stone-400 rounded-full"></div>
                    </div>

                    {/* Dark Backdrop Overlay */}
                    <div className="absolute inset-0 bg-stone-900/60 z-0"></div>

                    {/* Bottom Drawer Card */}
                    <div className="relative z-10 bg-white rounded-t-[28px] overflow-hidden shadow-xl border-t border-stone-200">
                      {/* Top Banner Image with 16:10 Ratio */}
                      <div className="w-full h-[180px] bg-stone-200 relative overflow-hidden">
                        {banner.imageUrl ? (
                          <img
                            src={banner.imageUrl}
                            alt="Banner Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=800&auto=format&fit=crop";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-stone-400 text-xs font-semibold">
                            <span>Image URL Missing</span>
                            <span className="text-[10px] mt-1 text-stone-400">800 × 500 px</span>
                          </div>
                        )}

                        {/* Frosted Close Button */}
                        <div className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-stone-900/60 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white text-xs font-bold cursor-pointer">
                          ✕
                        </div>
                      </div>

                      {/* Content Area */}
                      <div className="p-4 bg-white">
                        <h3 className="font-extrabold text-stone-900 text-sm leading-snug">
                          {banner.title || "Headline Title Here"}
                        </h3>
                        <p className="text-[11px] text-stone-500 mt-1 leading-relaxed">
                          {banner.subtitle || "Offer description and details will appear here."}
                        </p>

                        {/* Primary Button */}
                        <div className="mt-3.5 bg-[#FFCC0A] text-stone-950 font-bold text-xs py-2.5 px-4 rounded-xl text-center shadow-xs cursor-pointer hover:bg-amber-400 transition-colors">
                          {banner.buttonText || "Explore Services →"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-center text-[11px] text-stone-400 mt-4">
                  Matches customer app 1:1 aspect ratio and corner radius.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Banners;
